import { cn, StatusBadge } from "../../shared/ui";
import type { NotificationView } from "../../shared/types/api";
import {
  DUE_TONE_CLASS,
  dueLine,
  metaChips,
  relativeTime,
  SEVERITY_DOT,
} from "./notificationVocabulary";

/**
 * One notification, everywhere one is shown.
 *
 * <p>Shared by the bell and the history page on purpose. They are the same object read in two
 * places, and letting each draw its own version is how the panel and the page end up disagreeing
 * about what a notification says.
 *
 * <p>The row is built so that somebody who never opens it still knows whether it matters. What
 * happened, to which piece of work, in which project, how urgent, how far along, when it is due,
 * and who did it — the questions that would otherwise cost a click and a page load, answered on
 * five short lines.
 *
 * <p>Only one of those lines is allowed to raise its voice. The due date carries the colour and
 * the weight, because lateness is the single fact that changes what somebody does next; the rest
 * stays deliberately quiet so that the loud part still reads as loud.
 */
export function NotificationRow({
  notification,
  onOpen,
}: {
  notification: NotificationView;
  onOpen: (notification: NotificationView) => void;
}) {
  const due = dueLine(notification.context);
  const chips = metaChips(notification.context);
  const context = notification.context;

  return (
    <button
      type="button"
      onClick={() => onOpen(notification)}
      className={cn(
        "flex w-full items-stretch gap-3 px-4 py-3 text-left transition-colors hover:bg-ink-50",
        !notification.read && "bg-brand-50/60",
      )}
    >
      {/*
        A rail rather than a dot, doing two jobs with one mark: its colour is how urgent the
        server judged this, and its weight is whether the row has been read. Two separate
        indicators for two facts this closely related is one more thing to learn than necessary.
      */}
      <span
        className={cn(
          "w-0.5 shrink-0 rounded-full",
          SEVERITY_DOT[notification.severity],
          notification.read && "opacity-30",
        )}
        aria-hidden
      />

      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block text-sm",
            notification.read ? "text-ink-700" : "font-medium text-ink-900",
          )}
        >
          {notification.title}
        </span>

        {context?.itemTitle && (
          <span className="mt-0.5 block truncate text-[13px] text-ink-700">
            {context.itemTitle}
          </span>
        )}

        {notification.detail && (
          <span className="mt-1 block truncate text-xs italic text-ink-600">
            {notification.detail}
          </span>
        )}

        {(chips.length > 0 || context?.projectName) && (
          <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {chips.map((chip) => (
              <StatusBadge key={chip.label} tone={chip.tone}>
                {chip.label}
              </StatusBadge>
            ))}
            {context?.projectName && (
              <span className="truncate text-xs text-ink-500">{context.projectName}</span>
            )}
          </span>
        )}

        {due && (
          <span className={cn("mt-1 block text-xs", DUE_TONE_CLASS[due.tone])}>{due.text}</span>
        )}

        <span className="mt-1 block truncate text-xs text-ink-500">
          {[
            context?.assigneeName ? `Assigned to ${context.assigneeName}` : null,
            notification.actorName ? `by ${notification.actorName}` : null,
            relativeTime(notification.raisedAt),
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </span>
    </button>
  );
}
